import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Stack,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Bed as BedIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { checkMultipleAvailability } from '../../../services/bookingService';

// ───── Date parsing helpers ─────

function parseSpanishMonth(text) {
  const months = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
    ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
    jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11,
  };
  const lower = text.toLowerCase().trim();
  return months[lower] ?? null;
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateNice(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

/*  
  Parse date ranges from Spanish natural language. Supports:
  - "del 15 al 17 de junio"
  - "del 15 de junio al 17 de junio"
  - "15 junio al 17 junio"
  - "15-17 junio"
  - "15/06/2026 al 17/06/2026"
  - "2026-06-15 al 2026-06-17"
  - "junio 15 al 17"
  - "para el 20 de julio 2 noches"
*/
function parseDatesFromText(text) {
  const lower = text.toLowerCase().replace(/,/g, '').trim();
  const now = new Date();
  const currentYear = now.getFullYear();

  // Pattern: YYYY-MM-DD al YYYY-MM-DD
  let m = lower.match(/(\d{4})-(\d{1,2})-(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    return {
      checkIn: `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`,
      checkOut: `${m[4]}-${m[5].padStart(2,'0')}-${m[6].padStart(2,'0')}`,
    };
  }

  // Pattern: DD/MM/YYYY al DD/MM/YYYY
  m = lower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(?:al|a|hasta|-)\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    return {
      checkIn: `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`,
      checkOut: `${m[6]}-${m[5].padStart(2,'0')}-${m[4].padStart(2,'0')}`,
    };
  }

  // Pattern: "del DD al DD de MES (YYYY)?"
  m = lower.match(/(?:del?\s+)?(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) {
    const month = parseSpanishMonth(m[3]);
    if (month !== null) {
      const year = m[4] ? parseInt(m[4]) : (month >= now.getMonth() ? currentYear : currentYear + 1);
      return {
        checkIn: toYMD(new Date(year, month, parseInt(m[1]))),
        checkOut: toYMD(new Date(year, month, parseInt(m[2]))),
      };
    }
  }

  // Pattern: "del DD de MES al DD de MES (YYYY)?"
  m = lower.match(/(?:del?\s+)?(\d{1,2})\s+(?:de\s+)?(\w+)\s+(?:al|a|hasta)\s+(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) {
    const m1 = parseSpanishMonth(m[2]);
    const m2 = parseSpanishMonth(m[4]);
    if (m1 !== null && m2 !== null) {
      const year = m[5] ? parseInt(m[5]) : (m1 >= now.getMonth() ? currentYear : currentYear + 1);
      return {
        checkIn: toYMD(new Date(year, m1, parseInt(m[1]))),
        checkOut: toYMD(new Date(year, m2, parseInt(m[3]))),
      };
    }
  }

  // Pattern: "DD MES al DD MES" (without 'de')
  m = lower.match(/(\d{1,2})\s+(\w+)\s+(?:al|a|hasta)\s+(\d{1,2})\s+(\w+)(?:\s+(\d{4}))?/);
  if (m) {
    const m1 = parseSpanishMonth(m[2]);
    const m2 = parseSpanishMonth(m[4]);
    if (m1 !== null && m2 !== null) {
      const year = m[5] ? parseInt(m[5]) : (m1 >= now.getMonth() ? currentYear : currentYear + 1);
      return {
        checkIn: toYMD(new Date(year, m1, parseInt(m[1]))),
        checkOut: toYMD(new Date(year, m2, parseInt(m[3]))),
      };
    }
  }

  // Pattern: "MES DD al DD (YYYY)?"
  m = lower.match(/(\w+)\s+(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{1,2})(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) {
    const month = parseSpanishMonth(m[1]);
    if (month !== null) {
      const year = m[4] ? parseInt(m[4]) : (month >= now.getMonth() ? currentYear : currentYear + 1);
      return {
        checkIn: toYMD(new Date(year, month, parseInt(m[2]))),
        checkOut: toYMD(new Date(year, month, parseInt(m[3]))),
      };
    }
  }

  // Pattern: "DD de MES N noches"
  m = lower.match(/(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?\s+(\d+)\s*noches?/);
  if (m) {
    const month = parseSpanishMonth(m[2]);
    if (month !== null) {
      const year = m[3] ? parseInt(m[3]) : (month >= now.getMonth() ? currentYear : currentYear + 1);
      const ci = new Date(year, month, parseInt(m[1]));
      const co = new Date(ci);
      co.setDate(co.getDate() + parseInt(m[4]));
      return { checkIn: toYMD(ci), checkOut: toYMD(co) };
    }
  }

  // Pattern: just "N noches" with a previous single date "DD de MES"
  m = lower.match(/(\d{1,2})\s+(?:de\s+)?(\w+)\s+(\d+)\s*noches?/);
  if (m) {
    const month = parseSpanishMonth(m[2]);
    if (month !== null) {
      const year = month >= now.getMonth() ? currentYear : currentYear + 1;
      const ci = new Date(year, month, parseInt(m[1]));
      const co = new Date(ci);
      co.setDate(co.getDate() + parseInt(m[3]));
      return { checkIn: toYMD(ci), checkOut: toYMD(co) };
    }
  }

  // Pattern: single date "para el DD de MES (YYYY)?" or "el DD de MES" → 1 noche
  m = lower.match(/(?:para\s+)?(?:el\s+)?(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) {
    const month = parseSpanishMonth(m[2]);
    if (month !== null) {
      const year = m[3] ? parseInt(m[3]) : (month > now.getMonth() || (month === now.getMonth() && parseInt(m[1]) >= now.getDate()) ? currentYear : currentYear + 1);
      const ci = new Date(year, month, parseInt(m[1]));
      const co = new Date(ci);
      co.setDate(co.getDate() + 1);
      return { checkIn: toYMD(ci), checkOut: toYMD(co) };
    }
  }

  // Pattern: single ISO date "YYYY-MM-DD" → 1 noche
  m = lower.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const ci = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
    const co = new Date(ci);
    co.setDate(co.getDate() + 1);
    return { checkIn: toYMD(ci), checkOut: toYMD(co) };
  }

  // Pattern: single DD/MM/YYYY → 1 noche
  m = lower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const ci = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
    const co = new Date(ci);
    co.setDate(co.getDate() + 1);
    return { checkIn: toYMD(ci), checkOut: toYMD(co) };
  }

  return null;
}

function parseLugarFromText(text) {
  const lower = text.toLowerCase();
  if (/boutique/i.test(lower)) return 'boutique';
  if (/casa\s*hotel|casahotel/i.test(lower)) return 'casaHotel';
  return null; // search both
}

function calculateNights(checkIn, checkOut) {
  const [y1, m1, d1] = checkIn.split('-').map(Number);
  const [y2, m2, d2] = checkOut.split('-').map(Number);
  const a = new Date(y1, m1 - 1, d1);
  const b = new Date(y2, m2 - 1, d2);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// ───── Component ─────

const WELCOME_MSG = {
  role: 'bot',
  content: '¡Hola! 👋 Soy tu asistente de disponibilidad de **La Capilla Hotel**.\n\nPuedes preguntarme cosas como:\n- *"¿Hay disponibilidad para el 8 de abril en boutique?"* (1 noche)\n- *"Del 15 al 17 de junio"*\n- *"20 de julio 2 noches en boutique"*\n- *"Del 5 al 8 de agosto en casa hotel"*\n\nEscríbeme las fechas y yo busco por ti.',
};

export default function AvailabilityChatBot() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const addMessage = (msg) => setMessages(prev => [...prev, msg]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');

    // User message
    addMessage({ role: 'user', content: text });

    // Parse dates
    const dates = parseDatesFromText(text);
    if (!dates) {
      addMessage({
        role: 'bot',
        content: 'No pude detectar las fechas. 😅 Intenta con un formato como:\n- *"8 de abril en boutique"* (1 noche)\n- *"Del 15 al 17 de junio"*\n- *"20 de julio 2 noches"*\n- *"2026-08-10 al 2026-08-13"*',
      });
      return;
    }

    const nights = calculateNights(dates.checkIn, dates.checkOut);
    if (nights <= 0) {
      addMessage({
        role: 'bot',
        content: 'La fecha de salida debe ser posterior a la de entrada. Intenta de nuevo.',
      });
      return;
    }

    const lugar = parseLugarFromText(text);
    const lugarLabel = lugar === 'boutique' ? 'Hotel Boutique' : lugar === 'casaHotel' ? 'Casa Hotel' : 'ambos hoteles';

    addMessage({
      role: 'bot',
      content: `🔍 Buscando disponibilidad en **${lugarLabel}** para **${formatDateNice(dates.checkIn)}** al **${formatDateNice(dates.checkOut)}** (${nights} noche${nights > 1 ? 's' : ''})...`,
      loading: true,
    });

    setLoading(true);
    try {
      const result = await checkMultipleAvailability(dates.checkIn, dates.checkOut, lugar);

      if (result.error) {
        // Remove loading message and show error
        setMessages(prev => prev.filter(m => !m.loading));
        addMessage({ role: 'bot', content: `❌ Error: ${result.error}` });
        return;
      }

      const available = (result.rooms || []).filter(r => r.isAvailable);
      const unavailable = (result.rooms || []).filter(r => !r.isAvailable);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.loading));

      if (available.length === 0) {
        addMessage({
          role: 'bot',
          content: `😔 No hay habitaciones disponibles en **${lugarLabel}** del **${formatDateNice(dates.checkIn)}** al **${formatDateNice(dates.checkOut)}**.\n\nSe revisaron **${result.totalRooms || 0}** habitaciones y todas están ocupadas o bloqueadas en esas fechas.\n\n💡 ¿Quieres probar con otras fechas?`,
        });
      } else {
        addMessage({
          role: 'bot',
          content: `✅ ¡Encontré **${available.length}** habitación${available.length > 1 ? 'es' : ''} disponible${available.length > 1 ? 's' : ''}! (de ${result.totalRooms || 0} revisadas)`,
          rooms: available,
          unavailableRooms: unavailable,
          dates,
          nights,
        });
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => !m.loading));
      addMessage({ role: 'bot', content: `❌ Error al consultar disponibilidad. Verifica que el servidor esté corriendo.` });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([WELCOME_MSG]);
  };

  const getBookingUrl = (room, dates) => {
    const path = room.lugar === 'boutique' ? '/boutique' : '/reservas';
    return `${path}?checkIn=${dates.checkIn}&checkOut=${dates.checkOut}&room=${encodeURIComponent(room.roomName)}`;
  };

  const formatMXN = (n) => {
    if (!n && n !== 0) return '—';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
  };

  // ───── Render message ─────
  const renderMessage = (msg, idx) => {
    const isBot = msg.role === 'bot';

    return (
      <Box
        key={idx}
        sx={{
          display: 'flex',
          justifyContent: isBot ? 'flex-start' : 'flex-end',
          mb: 2,
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        {isBot && (
          <Avatar sx={{ bgcolor: '#d4af37', width: 32, height: 32, mt: 0.5 }}>
            <BotIcon sx={{ fontSize: 18 }} />
          </Avatar>
        )}

        <Box sx={{ maxWidth: isMobile ? '85%' : '70%', minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: isBot ? '#fafafa' : '#d4af37',
              color: isBot ? '#1a1a1a' : '#fff',
              border: isBot ? '1px solid #e0e0e0' : 'none',
            }}
          >
            {/* Text content with basic markdown-like rendering */}
            {msg.content.split('\n').map((line, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{
                  mb: line === '' ? 1 : 0.3,
                  lineHeight: 1.5,
                  '& strong': { fontWeight: 700 },
                }}
                dangerouslySetInnerHTML={{
                  __html: line
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                }}
              />
            ))}

            {/* Loading indicator */}
            {msg.loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} sx={{ color: '#d4af37' }} />
                <Typography variant="caption" color="text.secondary">Consultando...</Typography>
              </Box>
            )}
          </Paper>

          {/* Room results cards */}
          {msg.rooms && msg.rooms.length > 0 && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {msg.rooms.map((room, ri) => (
                <Card
                  key={ri}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #c8e6c9',
                    bgcolor: '#f1f8e9',
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                          <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ color: '#1a1a1a' }}>
                            {room.roomName}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip
                            icon={<HotelIcon />}
                            label={room.lugar === 'boutique' ? 'Boutique' : 'Casa Hotel'}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              bgcolor: room.lugar === 'boutique' ? '#e8eaf6' : '#fff3e0',
                              color: room.lugar === 'boutique' ? '#283593' : '#e65100',
                              border: `1px solid ${room.lugar === 'boutique' ? '#9fa8da' : '#ffcc80'}`,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {room.roomType}
                          </Typography>
                        </Stack>
                      </Box>
                      <Chip
                        label={`${room.availableUnits} disp.`}
                        size="small"
                        sx={{
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <BedIcon sx={{ fontSize: 14, color: '#666' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.totalUnits} unid.
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <GroupsIcon sx={{ fontSize: 14, color: '#666' }} />
                        <Typography variant="caption" color="text.secondary">
                          {room.availableUnits} de {room.totalUnits}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      href={getBookingUrl(room, msg.dates)}
                      target="_blank"
                      sx={{
                        mt: 1,
                        bgcolor: '#d4af37',
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        py: 0.5,
                        '&:hover': { bgcolor: '#b8960f' },
                      }}
                    >
                      Reservar ahora
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {/* Unavailable rooms summary */}
              {msg.unavailableRooms && msg.unavailableRooms.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    No disponibles ({msg.unavailableRooms.length}):
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {msg.unavailableRooms.map((r, ri) => (
                      <Chip
                        key={ri}
                        icon={<CancelIcon />}
                        label={r.roomName}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          bgcolor: '#ffebee',
                          color: '#c62828',
                          border: '1px solid #ef9a9a',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </Box>

        {!isBot && (
          <Avatar sx={{ bgcolor: '#1a1a1a', width: 32, height: 32, mt: 0.5 }}>
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
        )}
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #d4af37',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(100vh - 140px)' : 600,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#1a1a1a',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BotIcon sx={{ color: '#d4af37' }} />
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              Asistente de Disponibilidad
            </Typography>
            <Typography variant="caption" sx={{ color: '#d4af37' }}>
              La Capilla Hotel
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Limpiar chat">
          <IconButton size="small" onClick={handleClear} sx={{ color: '#fff' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: '#ffffff',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#d4af37', borderRadius: 3 },
        }}
      >
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Quick suggestions */}
      <Box sx={{ px: 2, py: 1, bgcolor: '#fafafa', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {[
          'Hoy 2 noches',
          'Este fin de semana boutique',
          'Próximo viernes 3 noches casa hotel',
        ].map((suggestion, i) => (
          <Chip
            key={i}
            label={suggestion}
            size="small"
            onClick={() => {
              // Build actual dates for quick suggestions
              const now = new Date();
              let q = suggestion;
              if (suggestion === 'Hoy 2 noches') {
                const ci = toYMD(now);
                const end = new Date(now);
                end.setDate(end.getDate() + 2);
                q = `del ${now.getDate()} al ${end.getDate()} de ${now.toLocaleDateString('es-MX', {month: 'long'})} ${now.getFullYear()}`;
              } else if (suggestion === 'Este fin de semana boutique') {
                const dayOfWeek = now.getDay();
                const daysUntilFri = dayOfWeek <= 5 ? 5 - dayOfWeek : 6;
                const fri = new Date(now);
                fri.setDate(fri.getDate() + daysUntilFri);
                const sun = new Date(fri);
                sun.setDate(sun.getDate() + 2);
                q = `del ${fri.getDate()} al ${sun.getDate()} de ${fri.toLocaleDateString('es-MX', {month: 'long'})} ${fri.getFullYear()} boutique`;
              } else {
                const dayOfWeek = now.getDay();
                const daysUntilFri = dayOfWeek <= 5 ? 5 - dayOfWeek : 6;
                const nextFri = new Date(now);
                nextFri.setDate(nextFri.getDate() + (daysUntilFri === 0 ? 7 : daysUntilFri));
                const mon = new Date(nextFri);
                mon.setDate(mon.getDate() + 3);
                q = `del ${nextFri.getDate()} al ${mon.getDate()} de ${nextFri.toLocaleDateString('es-MX', {month: 'long'})} ${nextFri.getFullYear()} casa hotel`;
              }
              setInput(q);
            }}
            sx={{
              cursor: 'pointer',
              bgcolor: '#fff',
              border: '1px solid #d4af37',
              color: '#1a1a1a',
              fontSize: '0.7rem',
              height: 26,
              '&:hover': { bgcolor: '#fdf6e3' },
            }}
          />
        ))}
      </Box>

      {/* Input */}
      <Box sx={{ p: 1.5, bgcolor: '#fff', borderTop: '1px solid #e0e0e0' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ej: del 15 al 17 de junio en boutique..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: '#fafafa',
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: '#d4af37' },
                '&.Mui-focused fieldset': { borderColor: '#d4af37' },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={loading || !input.trim()}
            sx={{
              bgcolor: '#d4af37',
              color: '#fff',
              '&:hover': { bgcolor: '#b8960f' },
              '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#999' },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SendIcon />}
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}
